/**
 * Task price breakdown — client-side port of the PMS backend helper at
 * prx_project_management_service/src/task/helper/task-price.helper.ts.
 *
 * A task's rate is the all-in rate the client quotes (e.g. 2195.00). GST is a
 * pass-through and BOCW labour cess is a statutory deduction the client withholds,
 * so neither is money the project earns. `uploadRate` is what remains — the working
 * rate every costing and P&L report uses.
 *
 *   baseRate   = round2(rate / (1 + gstPercent/100))
 *   gstAmount  = rate - baseRate
 *   cessAmount = round2(baseRate * cessPercent/100)
 *   uploadRate = rate - gstAmount - cessAmount
 *
 * Worked example (EXC-ADIT - Class II, 18% GST, 1% cess):
 *   2195.00 -> base 1860.17, gst 334.83, cess 18.60, upload 1841.57
 *
 * This must stay byte-identical to the backend helper — it drives the live preview in
 * the task form, and any divergence would show the user numbers the server won't store.
 * Arithmetic runs in integer micro-rupees (the decimal(20,6) scale of tasks.rate), and
 * the rate is quantised to whole paise first, so both identities close exactly:
 *   baseRate + gstAmount === rate
 *   uploadRate + gstAmount + cessAmount === rate
 */

export const DEFAULT_GST_PERCENT = 18;
export const DEFAULT_CESS_PERCENT = 1;

export interface TaskPriceBreakdown {
  /** The contract rate quantised to whole paise. */
  rate: number;
  baseRate: number;
  gstAmount: number;
  cessAmount: number;
  uploadRate: number;
}

const MICRO = 1_000_000;
const MICRO_PER_PAISA = MICRO / 100;

const toMicro = (rupees: number): number => Math.round(rupees * MICRO);
const toRupees = (micro: number): number => micro / MICRO;
const roundToPaisa = (micro: number): number =>
  Math.round(micro / MICRO_PER_PAISA) * MICRO_PER_PAISA;

const asFiniteNumber = (value: unknown, fallback: number): number => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function computeTaskPriceBreakdown(
  rateInclGst: unknown,
  gstPercent: unknown = DEFAULT_GST_PERCENT,
  cessPercent: unknown = DEFAULT_CESS_PERCENT,
): TaskPriceBreakdown {
  const rate = asFiniteNumber(rateInclGst, 0);
  const gstPct = asFiniteNumber(gstPercent, DEFAULT_GST_PERCENT);
  const cessPct = asFiniteNumber(cessPercent, DEFAULT_CESS_PERCENT);

  if (rate <= 0) {
    return { rate: 0, baseRate: 0, gstAmount: 0, cessAmount: 0, uploadRate: 0 };
  }

  const rateMicro = roundToPaisa(toMicro(rate));
  const baseMicro = roundToPaisa(rateMicro / (1 + gstPct / 100));
  const gstMicro = rateMicro - baseMicro;
  const cessMicro = roundToPaisa((baseMicro * cessPct) / 100);
  const uploadMicro = rateMicro - gstMicro - cessMicro;

  return {
    rate: toRupees(rateMicro),
    baseRate: toRupees(baseMicro),
    gstAmount: toRupees(gstMicro),
    cessAmount: toRupees(cessMicro),
    uploadRate: toRupees(uploadMicro),
  };
}
